<template>
    <div class="manage-units">
        <!-- Toolbar -->
        <div class="manage-units__toolbar">
            <div class="manage-units__building-info">
                <p class="manage-units__building-address">{{ buildings.selectedBuilding.address_line_1 }}</p>
                <p class="manage-units__building-location">{{ buildings.selectedBuilding.city }}, {{ buildings.selectedBuilding.state }}</p>
            </div>
            <themed-button
                @click="createNewUnit"
                variant="default"
                size="sm"
            >
                <theme-icon icon="add" type="icons" class="manage-units__add-icon" />
                New Unit
            </themed-button>
        </div>

        <!-- Content -->
        <div class="manage-units__content">
            <!-- Unit List View -->
            <div v-if="!units.selectedUnitId" class="manage-units__list">
                <themed-table
                    :columns="columns"
                    :data="buildings.selectedBuilding.units"
                    :pagination="true"
                    :per-page-options="[10, 25, 50]"
                    :hoverable="true"
                    aria-label="Units table"
                >
                    <template #column-actions="{ row }">
                        <div class="manage-units__actions">
                            <themed-button
                                @click="units.selectedUnitId = row.id"
                                variant="ghost"
                                size="sm"
                            >
                                Edit
                            </themed-button>
                            <themed-button
                                @click="confirmDeleteUnit(row.id)"
                                variant="ghost"
                                size="sm"
                                class="manage-units__delete-button"
                            >
                                Delete
                            </themed-button>
                        </div>
                    </template>

                    <template #empty-state>
                        <div class="manage-units__empty">
                            <span class="manage-units__empty-text">No Units Found</span>
                            <themed-button
                                @click="createNewUnit"
                                variant="default"
                                size="sm"
                            >
                                <theme-icon icon="add" type="icons" class="manage-units__add-icon" />
                                Create Unit
                            </themed-button>
                        </div>
                    </template>
                </themed-table>
            </div>

            <!-- Unit Edit View -->
            <div v-else class="manage-units__edit">
                <!-- Breadcrumb -->
                <div class="manage-units__breadcrumb">
                    <a @click="showUnitList" class="manage-units__breadcrumb-link">Units</a>
                    <span class="manage-units__breadcrumb-separator">/</span>
                    <span class="manage-units__breadcrumb-current">
                        {{ units.selectedUnitId == 'new' ? 'New Unit' : 'Edit Unit' }}
                    </span>
                </div>

                <!-- Section Title -->
                <h3 class="manage-units__section-title">Unit</h3>

                <!-- Unit Form -->
                <div v-if="selectedUnit" class="manage-units__form">
                    <div class="manage-units__form-row">
                        <themed-form-field label="FLOOR" :error="errors.floor">
                            <themed-select
                                v-model="selectedUnit.floor"
                                :options="floorOptions"
                                placeholder="Select"
                            />
                        </themed-form-field>

                        <themed-form-field label="SUITE" :error="errors.suite">
                            <themed-input
                                v-model="selectedUnit.suite"
                                placeholder="Enter"
                            />
                        </themed-form-field>

                        <themed-form-field label="STATUS" :error="errors.status">
                            <themed-select
                                v-model="selectedUnit.status"
                                :options="statusOptions"
                                placeholder="Select"
                            />
                        </themed-form-field>

                        <themed-form-field label="SIZE" :error="errors.size">
                            <themed-number-input
                                v-model="selectedUnit.size"
                                placeholder="Enter"
                            />
                        </themed-form-field>

                        <themed-form-field label="DIVISIBLE" :error="errors.divisible">
                            <themed-number-input
                                v-model="selectedUnit.divisible"
                                placeholder="Enter"
                            />
                        </themed-form-field>

                        <themed-form-field label="RENEWAL SPACE?" :error="errors.is_renewal_space">
                            <themed-select
                                v-model="selectedUnit.is_renewal_space"
                                :options="renewalOptions"
                                placeholder="Select"
                            />
                        </themed-form-field>

                        <themed-form-field label="TI ALLOWANCE" :error="errors.ti_allowance">
                            <themed-currency-input
                                v-model="ti_allowance"
                                is-decimal
                            />
                        </themed-form-field>
                    </div>

                    <themed-form-field label="Comments" :error="errors.comment" class="manage-units__comments-field">
                        <themed-textarea
                            v-model="selectedUnit.comment"
                            :rows="6"
                            placeholder="Enter comments"
                        />
                    </themed-form-field>
                </div>
                <div v-else class="manage-units__loading">
                    Loading unit data...
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedNumberInput from '../form/ThemedNumberInput.vue';
import ThemedCurrencyInput from '../form/ThemedCurrencyInput.vue';
import ThemedTextarea from '../form/ThemedTextarea.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
    name: 'ManageUnits',

    components: {
        ThemedTable,
        ThemedButton,
        ThemedFormField,
        ThemedInput,
        ThemedSelect,
        ThemedNumberInput,
        ThemedCurrencyInput,
        ThemedTextarea,
        ThemeIcon
    },

    props: {
        selectedSection: {
            type: String,
            default: 'unit_info'
        }
    },

    data() {
        return {
            currentSection: this.selectedSection,
            ti_allowance: '',
            errors: {},
            loading: false
        }
    },

    created() {
        this.expenses.all = this.selectedUnit?.expenses || [];
    },

    mounted() {
        this.ti_allowance = this.ti;

        this.$eventHub.$on('change-section', (section) => {
            this.currentSection = section;
        });
    },

    watch: {
        selectedUnit() {
            if (this.selectedUnitId == null || this.selectedUnitId == 'new') {
                this.ti_allowance = '';
            } else {
                this.expenses.all = this.selectedUnit.expenses || [];
                this.ti_allowance = this.ti;
            }
        },

        loading(newValue) {
            this.$emit('loading-change', newValue);
        }
    },

    computed: {
        ...mapState(['buildings', 'units', 'expenses']),
        ...mapGetters(['selectedUnit']),

        columns() {
            return [
                { 
                    field: 'floor', 
                    label: 'Floor', 
                    sortable: true,
                    width: '100px',
                    align: 'center'
                },
                { 
                    field: 'suite', 
                    label: 'Suite', 
                    sortable: true,
                    width: '120px',
                    align: 'center'
                },
                { 
                    field: 'size', 
                    label: 'Size', 
                    sortable: true,
                    width: '120px',
                    align: 'right',
                    formatter: (value) => value ? this.$options.filters.number(value) : '-'
                },
                { 
                    field: 'divisible', 
                    label: 'Divisible', 
                    sortable: true,
                    width: '120px',
                    align: 'right',
                    formatter: (value) => value ? this.$options.filters.number(value) : '-'
                },
                { 
                    field: 'status', 
                    label: 'Status', 
                    sortable: true,
                    width: '120px',
                    align: 'right'
                },
                { 
                    field: 'ti_allowance', 
                    label: 'TI Allowance', 
                    sortable: true,
                    width: '140px',
                    align: 'right',
                    formatter: (value) => this.formatCurrency(value, 2)
                },
                { 
                    field: 'updated_at', 
                    label: 'Last Update', 
                    sortable: true,
                    width: '140px',
                    align: 'right',
                    formatter: (value) => value ? this.$options.filters.date(value) : '-'
                },
                {
                    field: 'actions',
                    label: '',
                    type: 'actions',
                    width: '160px',
                    sortable: false
                }
            ];
        },

        sections() {
            return {
                unit_info: {
                    label: "Unit",
                    enabled: true
                },
                expenses: {
                    label: "Expenses",
                    enabled: this.units.selectedUnitId != 'new',
                    showCount: true,
                    count: this.expenses.all?.length || 0
                }
            };
        },

        aUnitIsSelected() {
            return this.units.selectedUnitId != 'new' && this.units.selectedUnitId != '';
        },

        ti: {
            get() {
                return this.selectedUnit ? this.formatCurrency(this.selectedUnit.ti_allowance, 2) : 0;
            },
            set(newValue) {
                this.ti_allowance = newValue;
            }
        },

        floorOptions() {
            const floors = this.buildings.selectedBuilding.floors || 1;
            return Array.from({ length: floors }, (_, i) => ({
                value: i + 1,
                label: (i + 1).toString()
            }));
        },

        statusOptions() {
            return [
                { value: 'Available', label: 'Available' },
                { value: 'Occupied', label: 'Occupied' },
                { value: 'Sublease', label: 'Sublease' },
                { value: 'Vacant', label: 'Vacant' }
            ];
        },

        renewalOptions() {
            return [
                { value: 0, label: 'No' },
                { value: 1, label: 'Yes' }
            ];
        },

        currentUnitIndex() {
            if (!this.units.selectedUnitId || !this.buildings.selectedBuilding.units) {
                return -1;
            }
            return this.buildings.selectedBuilding.units.findIndex(
                unit => unit.id === this.units.selectedUnitId
            );
        },

        hasPreviousUnit() {
            return this.currentUnitIndex > 0;
        },

        hasNextUnit() {
            return this.currentUnitIndex >= 0 && 
                   this.currentUnitIndex < this.buildings.selectedBuilding.units.length - 1;
        }
    },

    methods: {
        hideManageUnitsModal() {
            if (this.units.selectedUnitId == 'new') {
                this.removeUnit('new');
            }
            this.$modal.hide('manage-units');
        },

        showUnitList() {
            this.removeUnit('new');
            this.units.selectedUnitId = null;
        },

        createNewUnit() {
            // Check if 'new' unit already exists to prevent duplicates
            const existingNewUnit = _.find(this.units.all, ['id', 'new']);
            
            if (!existingNewUnit) {
                this.units.all.push({
                    id: 'new',
                    building_id: this.buildings.selectedBuilding.id,
                    expenses: [],
                    status: null,
                    is_renewal_space: null,
                    size: null,
                    divisible: null,
                    floor: this.buildings.selectedBuilding.floors ? null : 1,
                    suite: null,
                    ti_allowance: null,
                    comment: null
                });
            }

            this.expenses.mode = null;
            this.expenses.selectedExpenseId = null;
            this.expenses.all = [];
            this.units.selectedUnitId = 'new';
            this.currentSection = 'unit_info';
        },

        createUnit() {
            this.loading = true;

            axios.post('/units', this.selectedUnit)
                .then((response) => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });

                    this.buildings.selectedBuilding.units = _.remove(
                        this.buildings.selectedBuilding.units,
                        (unit) => unit.id != 'new'
                    );

                    this.units.all = _.remove(this.units.all, (unit) => unit.id != 'new');

                    this.buildings.selectedBuilding.units.push(response.data.unit);
                    this.expenses.all = [];
                    this.units.all.push(response.data.unit);
                    this.units.filtered.push(response.data.unit);
                    this.units.selectedUnitId = response.data.unit.id;

                    // Emit success event to parent
                    this.$emit('unit-saved');
                })
                .catch((error) => {
                    console.error(error);
                    this.errors = error.response.data.errors || {};
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        updateUnit() {
            this.loading = true;

            axios.patch('/units/' + this.selectedUnit.id, this.selectedUnit)
                .then((response) => {
                    this.selectedUnit.ti_allowance = this.formatCurrency(this.selectedUnit.ti_allowance, 2);

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });

                    // Emit success event to parent
                    this.$emit('unit-saved');
                })
                .catch((error) => {
                    console.error(error);
                    this.errors = error.response.data.errors || {};
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        saveUnit() {
            this.errors = {};
            this.selectedUnit.ti_allowance = this.prepCurrency(this.ti_allowance);

            if (this.units.selectedUnitId == "new") {
                this.createUnit();
            } else {
                this.updateUnit();
            }
        },

        async saveExpense() {
            this.errors = {};

            await this.$store.dispatch('save').then(() => {
                this.expenses.mode = null;
                this.expenses.selectedExpenseId = null;

                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: "Expense Saved"
                });
            });
        },

        confirmDeleteUnit(unitId) {
            this.$swal({
                title: 'Delete this unit?',
                text: "ALL data will be lost, including any lease information attached to this unit",
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteUnit(unitId);
                }
            });
        },

        deleteUnit(unitId) {
            if (unitId == 'new') {
                this.removeUnit(unitId);
                return;
            }

            axios.delete('/units/' + unitId)
                .then((response) => {
                    this.removeUnit(unitId);

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                })
                .catch((error) => {
                    console.error(error);
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                });
        },

        removeUnit(unitId) {
            if (unitId == 'new') {
                this.units.all = _.remove(this.units.all, (unit) => unit.id != unitId);
                return;
            }

            const unit = _.find(this.buildings.selectedBuilding.units, ['id', unitId]);

            if (!unit) {
                return;
            }

            const stackSize = this.buildings.selectedBuilding.stack?.[unit.floor]?.[unit.status];

            // Remove the unit
            this.buildings.selectedBuilding.units = _.remove(
                this.buildings.selectedBuilding.units,
                (unit) => unit.id != unitId
            );
            this.units.all = _.remove(this.units.all, (unit) => unit.id != unitId);

            // Update the stack chart
            if (stackSize !== undefined) {
                this.buildings.selectedBuilding.stack[unit.floor][unit.status] = stackSize - unit.size;
            }
        },

        navigateToPreviousUnit() {
            if (this.hasPreviousUnit) {
                const previousUnit = this.buildings.selectedBuilding.units[this.currentUnitIndex - 1];
                this.units.selectedUnitId = previousUnit.id;
            }
        },

        navigateToNextUnit() {
            if (this.hasNextUnit) {
                const nextUnit = this.buildings.selectedBuilding.units[this.currentUnitIndex + 1];
                this.units.selectedUnitId = nextUnit.id;
            }
        }
    }
}
</script>
